// Global
import React from 'react';
import CtaLink, { LinkContext } from 'components/helpers/Atoms/Cta/CtaLink';
import styles from './HeaderDesktop.module.scss';
import { PureHeaderPrimaryLinkBar } from '../types';
import { SvgIcon } from 'components/helpers/SvgIcon';
import useDictionary from 'lib/hooks/useDictionary';
import clsx from 'clsx';
interface HeaderDesktopProps {
  primaryLinkBar: PureHeaderPrimaryLinkBar;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  primaryLinkBarIndex: number;
  linkContext: LinkContext;
}

export const HeaderDesktop = (props: HeaderDesktopProps) => {
  const { primaryLinkBar, linkContext } = props;
  const { getDictionaryValue } = useDictionary();
  const showQuickLinks = !primaryLinkBar.hideQuickLinks && primaryLinkBar.quickLinksSection;

  return (
    <>
      {/* Mega menu styles for dropdown menu link */}
      {primaryLinkBar.isNavDropDownMenuLink ? (
        <>
          {(primaryLinkBar?.primaryLinks?.length ?? 0) > 0 && (
            <ul className={styles.megaMenuDropdownContainerDesktop}>
              <li className={styles.megaLinkWrapper}>
                <CtaLink
                  text={primaryLinkBar?.primaryLinkLabel}
                  url={primaryLinkBar?.primaryLinkUrl}
                  ctaStyle="none"
                  className={styles.menuLinkStyle}
                  target={primaryLinkBar?.primaryLinkTarget}
                  linkContext={linkContext}
                  analyticsHeader={primaryLinkBar?.primaryLinkLabel}
                />
              </li>
              {primaryLinkBar?.primaryLinks?.map((link, index) => (
                <li key={index} className={styles.megaLinkWrapper}>
                  <CtaLink
                    text={link.headerLinkText}
                    url={link.headerLinkUrl}
                    ctaStyle="none"
                    className={styles.menuLinkStyle}
                    target={link.headerLinkTarget}
                    linkContext={linkContext}
                    analyticsHeader={primaryLinkBar?.primaryLinkLabel}
                  />
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        // Mega menu styles for default and dropdown menu link
        <div className={styles.megaMenuContainerDesktop}>
          {showQuickLinks && (
            <div className={styles.megaMenuSidebar}>
              <div className={styles.megaMenuSidebarLinks}>
                <div className={styles.megaMenuTitle}>{primaryLinkBar?.primaryLinkLabel}</div>
                <>
                  <CtaLink
                    text={showQuickLinks.quickLinksSectionViewAllTitle}
                    url={showQuickLinks.quickLinksSectionViewAllUrl}
                    ctaStyle="none"
                    ctaIconAfter="arrow-dash-right"
                    ctaIconSize="sm-plus-12"
                    className={styles.megaMenuHeaderLink}
                    target={showQuickLinks.quickLinksSectionViewAllTarget}
                    linkContext={linkContext}
                    analyticsHeader={primaryLinkBar?.primaryLinkLabel}
                    analyticsSubHeader={showQuickLinks.quickLinkSectionTitle}
                  />
                  <ul className={styles.quickLinkSectionWrapper}>
                    <li>
                      <div
                        className={styles.quickLinkSectionTitle}
                        id={`quick-link-section-title-${props.primaryLinkBarIndex}`}
                      >
                        {showQuickLinks.quickLinkSectionTitle ||
                          getDictionaryValue('Navigation.Header.QuickLinks')}
                      </div>
                      <ul
                        className={styles.quickLinkSectionList}
                        aria-labelledby={`quick-link-section-title-${props.primaryLinkBarIndex}`}
                      >
                        {showQuickLinks.quickLinkSectionUrls.map((link, index) => (
                          <li className={styles.megaLinkWrapper} key={index}>
                            <CtaLink
                              text={link.quickLinkSectionLabel}
                              url={link.quickLinkSectionUrl}
                              ctaStyle="none"
                              className={styles.menuLinkStyle}
                              target={link.quickLinkSectionTarget}
                              linkContext={linkContext}
                              analyticsHeader={primaryLinkBar?.primaryLinkLabel}
                              analyticsSubHeader={showQuickLinks.quickLinkSectionTitle}
                            />
                          </li>
                        ))}
                      </ul>
                    </li>
                  </ul>
                </>
              </div>
            </div>
          )}
          <div className={styles.megaMenuSectionWrapper}>
            <ul className={styles.megaMenuSectionListGrid}>
              {primaryLinkBar?.sectionLinks?.map((link, index) => {
                if (link.sectionLinkLabel || link.sectionLinkList.length > 0) {
                  const rowsPerCol = link.sectionLinksPerColumn || 6;
                  const numCols = Math.ceil(link.sectionLinkList.length / rowsPerCol);
                  return (
                    <li
                      key={index}
                      className={styles.megaMenuSectionItemWrapper}
                      style={{ gridColumn: `span ${numCols}` }}
                    >
                      {link.sectionLinkLabel && link.sectionLinkUrl ? (
                        <CtaLink
                          text={link.sectionLinkLabel}
                          url={link.sectionLinkUrl}
                          ctaStyle="none"
                          className={styles.megaMenuHeaderLink}
                          ctaIconAfter="arrow-dash-right"
                          ctaIconSize="sm-plus-12"
                          id={`section-link-${props.primaryLinkBarIndex}-${index}`}
                          target={link.sectionLinkTarget}
                          linkContext={linkContext}
                          analyticsHeader={primaryLinkBar?.primaryLinkLabel}
                          analyticsSubHeader={link.sectionLinkLabel}
                        />
                      ) : (
                        <span
                          className={styles.megaMenuHeaderItem}
                          id={`section-link-${props.primaryLinkBarIndex}-${index}`}
                        >
                          {link.sectionLinkLabel}
                        </span>
                      )}
                      {link.sectionLinkList && link.sectionLinkList.length > 0 && (
                        <ul
                          className={styles.sectionLinkColumn}
                          style={{
                            gridTemplateRows: `repeat(${rowsPerCol}, auto)`,
                          }}
                          aria-labelledby={`section-link-${props.primaryLinkBarIndex}-${index}`}
                        >
                          {link.sectionLinkList.map((sectionListLink, index) => (
                            <li
                              key={index}
                              className={clsx(styles.megaLinkWrapper, styles.sectionLinkColumnItem)}
                            >
                              <CtaLink
                                text={sectionListLink.sectionLinkLabel}
                                url={sectionListLink.sectionLinkUrl}
                                ctaStyle="none"
                                className={styles.menuLinkStyle}
                                target={sectionListLink.sectionLinkTarget}
                                linkContext={linkContext}
                                analyticsHeader={primaryLinkBar?.primaryLinkLabel}
                                analyticsSubHeader={link.sectionLinkLabel}
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                }
                return null;
              })}
            </ul>
            <button
              aria-label={getDictionaryValue('Navigation.Header.Close') || 'Close'}
              className={styles.closeButtonWrapper}
              onClick={() => props.setIsOpen(false)}
            >
              <SvgIcon icon="menu-close" size="sm" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
